/**
 * GridCrash — Board Themes
 *
 * Maps the equipped cosmetic theme to board icon glyphs. The default
 * theme keeps the existing inline SVGs in board.ts (icons undefined
 * here means "use the SVG"); purchased themes swap flags/mines for
 * emoji and override the header mascot.
 *
 * The active theme id comes from the profile cache, so it works
 * offline and applies instantly after an equip.
 */

import { getCachedProfile } from "./profile.js";
import { getEquippedLocal } from "./wallet.js";

export type ThemeId =
  | "default" | "us_holiday" | "winter"
  | "halloween" | "ocean" | "space" | "retro" | "neon" | "jungle";

export interface BoardTheme {
  id: ThemeId;
  label: string;
  /** Emoji glyphs; a missing slot falls back to the default SVG icon. */
  icons: { flag?: string; mine?: string };
  /** Header mascot override while the theme is equipped. */
  mascotOverride?: string;
}

export const BOARD_THEMES: Record<ThemeId, BoardTheme> = {
  default: {
    id: "default",
    label: "Classic",
    icons: {},
  },
  us_holiday: {
    id: "us_holiday",
    label: "US Holiday",
    icons: { flag: "🇺🇸", mine: "🎆" },
    mascotOverride: "😎",
  },
  winter: {
    id: "winter",
    label: "Winter",
    icons: { flag: "🎄", mine: "❄️" },
    mascotOverride: "🥶",
  },
  halloween: {
    id: "halloween",
    label: "Halloween",
    icons: { flag: "🎃", mine: "👻" },
    mascotOverride: "🧛",
  },
  ocean: {
    id: "ocean",
    label: "Ocean",
    icons: { flag: "🐚", mine: "🦈" },
    mascotOverride: "🐙",
  },
  space: {
    id: "space",
    label: "Space",
    icons: { flag: "🛸", mine: "☄️" },
    mascotOverride: "👨‍🚀",
  },
  retro: {
    id: "retro",
    label: "Retro",
    icons: { flag: "🕹️", mine: "💾" },
    mascotOverride: "😜",
  },
  neon: {
    id: "neon",
    label: "Neon",
    icons: { flag: "⚡", mine: "🔮" },
    mascotOverride: "🤖",
  },
  jungle: {
    id: "jungle",
    label: "Jungle",
    icons: { flag: "🌺", mine: "🐍" },
    mascotOverride: "🐵",
  },
};

export function getActiveThemeId(): ThemeId {
  const equipped = getCachedProfile()?.equipped_theme ?? getEquippedLocal("theme");
  return equipped && equipped in BOARD_THEMES ? (equipped as ThemeId) : "default";
}

export function getActiveTheme(): BoardTheme {
  return BOARD_THEMES[getActiveThemeId()];
}

/** Mascot glyph for the equipped theme, or null to use the unlock mascot. */
export function getThemeMascot(): string | null {
  return getActiveTheme().mascotOverride ?? null;
}
